import { useState, useEffect } from "react";
import { ArrowRight, X } from "lucide-react";
import { supabase } from '../SupabaseClient';
import { message } from 'antd';

export default function CTA() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        businessName: "",
        businessType: "",
        email: "",
        phone: "",
        service: "",
    });

    useEffect(() => {
        const timeout = setTimeout(() => {
            document.body.style.transform = 'translateZ(0)';
        }, 10);
        return () => clearTimeout(timeout);
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const { data, error } = await supabase
                .from('leads')
                .insert([
                    {
                        name: form.name,
                        business_name: form.businessName,
                        business_type: form.businessType,
                        email: form.email,
                        phone: form.phone,
                        service: form.service,
                        created_at: new Date().toISOString()
                    }
                ]);

            if (error) throw error;

            console.log("Form submitted successfully:", data);
            message.success("Thank you! We'll be in touch soon.");

            if (window.gtag) {
                window.gtag('event', 'generate_lead', {
                    method: 'popup_form'
                });
            }

            setForm({
                name: "",
                businessName: "",
                businessType: "",
                email: "",
                phone: "",
                service: "",
            });

            setOpen(false);
        } catch (error) {
            console.error("Error submitting form:", error);
            message.error("There was an error submitting the form. Please try again.");
        }
    };

    return (
        <section id="cta" className="relative py-8 pb-20 px-6">
            <div className="max-w-5xl mx-auto">
                <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-emerald-600 rounded-3xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-emerald-500/20" />
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />

                    <div className="relative z-10 p-12 md:p-16 text-center">
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            Ready to Transform
                            <br />
                            Your Business?
                        </h2>

                        <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed">
                            Join innovative companies already using NeuroStudio to automate workflows,
                            engage customers, and scale operations.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                            <button
                                className="group px-10 py-5 bg-white text-blue-600 rounded-full font-bold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-2xl"
                                onClick={() => setOpen(true)}
                            >
                                <span>Get Started</span>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>

                            <button
                                onClick={() => window.open('https://calendar.google.com/calendar/appointments/schedules/AcZssZ295SkqY51sm3o4TmShmYFw3C5ok3qDn-33ZBq42EOG6Is-9i2mt6oVaB73BndN5R8FqCWulG8a', '_blank')}
                                className="px-10 py-5 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300"
                            >
                                Schedule Demo
                            </button>
                        </div>

                        {open && (
                            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                                <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 w-full max-w-lg relative shadow-2xl">
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="absolute top-4 right-4 text-white hover:text-red-400"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>

                                    <h2 className="text-3xl font-bold text-white mb-6 text-center">
                                        Start Your AI Journey
                                    </h2>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Your Name"
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                        />

                                        <input
                                            type="text"
                                            name="businessName"
                                            placeholder="Business Name"
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                            value={form.businessName}
                                            onChange={handleChange}
                                        />

                                        <input
                                            type="text"
                                            name="businessType"
                                            placeholder="Business Industry/Type"
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                            value={form.businessType}
                                            onChange={handleChange}
                                        />

                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Email (required)"
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                        />

                                        <input
                                            type="tel"
                                            name="phone"
                                            placeholder="Phone Number (optional)"
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                            value={form.phone}
                                            onChange={handleChange}
                                        />

                                        <select
                                            name="service"
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white"
                                            value={form.service}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="" className="text-slate-900">
                                                Select a Service
                                            </option>
                                            <option value="AI Agent" className="text-slate-900">AI Agent / Employee</option>
                                            <option value="Chatbot" className="text-slate-900">Chatbot</option>
                                            <option value="Backend Automation" className="text-slate-900">Backend Automation</option>
                                            <option value="Web/App Design" className="text-slate-900">Web / App Design</option>
                                            <option value="Custom Suite" className="text-slate-900">Custom Suite</option>
                                        </select>

                                        <button
                                            type="submit"
                                            className="w-full py-3 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-lg text-white font-semibold hover:opacity-90"
                                        >
                                            Submit
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}